import { BusinessOutlined, EditOutlined, EmailOutlined, PersonOutline } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import FloatingBottomButton from '@/components/common/FloatingBottomButton';
import { useSendReset } from '@/generated/client/passwordreset/passwordreset';
import type { DetailedUserDto } from '@/generated/model';
import { useNotificationStore } from '@/stores/notificationstore';

import { createUserMutationErrorHandler } from '../../api/userMutationErrors';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { canManageUsers, getUserDetailActions } from '../../utils/userAccessControlPermissions';
import { getLanguageCode } from '../../utils/languageUtils';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { getUserDisplayName } from '../../utils/userNameUtils';
import { UserStatusIcon } from '../user-status-icon/UserStatusIcon';
import { UserDetailsPermissions } from './UserDetailsPermissions';
import { UserDetailsSection } from './UserDetailsSection';
import { UserDetailsStateAlert } from './UserDetailsStateAlert';

type UserDetailsContentProps = {
  userId: number;
  user: DetailedUserDto;
  header: ReactElement;
};

export function UserDetailsContent({ userId, user, header }: UserDetailsContentProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userCtx = useCurrentUser();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const language = i18n.resolvedLanguage ?? i18n.language;
  const actions = getUserDetailActions({
    state: user.state,
    isCurrentUser: userId === userCtx?.user.id,
    canManageUsers: canManageUsers(userCtx?.user),
  });
  const displayName = getUserDisplayName(
    {
      firstName: user.firstName,
      surname: user.surname,
      name: user.username,
    },
    language
  );
  const handleMutationError = createUserMutationErrorHandler({
    operation: 'resend-invitation',
    showNotification,
    t,
  });
  // The backend intentionally reuses the password-reset endpoint for invitation emails when `fromInvitation` is true.
  const resendInvitation = useSendReset({
    mutation: {
      onSuccess: () => {
        showNotification('success', t('userAccessControl.details.invitationResent'));
      },
      onError: handleMutationError,
    },
  });

  function handleResendInvitation() {
    resendInvitation.mutate({
      data: {
        desiredUser: user.username,
        desiredEmail: user.username,
        lang: getLanguageCode(language),
      },
    });
  }

  return (
    <Stack position="relative" width="100%" height="100%" minHeight={0} bgcolor="background.paper">
      {header}

      <Stack p={1.5} pb={10} spacing={2} overflow="auto" flex={1} minHeight={0}>
        <UserDetailsSection icon={<PersonOutline />} title={t('userAccessControl.details.userData')}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box minWidth={0}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {displayName}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" color="text.secondary">
                <EmailOutlined fontSize="small" />
                <Typography variant="body2" noWrap>
                  {user.username}
                </Typography>
              </Stack>
              {user.phone && (
                <Typography variant="body2" color="text.secondary">
                  {user.phone}
                </Typography>
              )}
            </Box>
            <UserStatusIcon state={user.state} />
          </Stack>
        </UserDetailsSection>

        <UserDetailsStateAlert
          state={user.state}
          canResendInvitation={actions.canResendInvitation}
          isResendingInvitation={resendInvitation.isPending}
          onResendInvitation={handleResendInvitation}
        />

        <UserDetailsSection icon={<BusinessOutlined />} title={t('userAccessControl.details.customer')}>
          <Typography variant="body1">
            {user.role.roleObjectName ?? t('userAccessControl.details.notAvailable')}
          </Typography>
        </UserDetailsSection>

        <UserDetailsPermissions user={user} language={language} />
      </Stack>

      {actions.canEdit && (
        <FloatingBottomButton
          icon={<EditOutlined />}
          title={t('userAccessControl.details.edit')}
          onAction={() => navigate(userAccessRoutes.editUser(userId))}
          position="absolute"
        />
      )}
    </Stack>
  );
}
